import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #fff;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 4px;
`;

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const StyledSelectBox = styled.div`
  width: 100%;
  background: #25253C;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.size === 'small' ? '8px' : '12px'};
  padding: ${props => props.size === 'small' ? '7px 30px 7px 10px' : '14px 30px 14px 16px'};
  font-family: 'Clash Grotesk', sans-serif;
  font-size: ${props => props.size === 'small' ? '0.95rem' : '1.1rem'};
  font-weight: 500;
  cursor: pointer;
  outline: none;
  transition: border 0.2s;
  position: relative;
  display: flex;
  align-items: center;
  &:focus, &.focused {
    border-color: #25253C;
  }
  &:disabled {
    background: #25253C;
    color: #aaa;
    cursor: not-allowed;
  }
`;

const Arrow = styled.span`
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #fff;
  font-size: 0.8rem;
`;

const Dropdown = styled.ul`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  background: #313151;
  color: #fff;
  border-radius: ${props => props.size === 'small' ? '8px' : '12px'};
  box-shadow: 0 4px 24px rgba(0,0,0,0.18);
  margin: 0;
  padding: 8px 8px 12px 8px;
  z-index: 1000;
  list-style: none;
  border: 1px solid rgba(255,255,255,0.12);
  max-height: 220px;
  overflow-y: auto;
`;

const Option = styled.li`
  padding: ${props => props.size === 'small' ? '7px 16px' : '14px 16px'};
  font-family: 'Clash Grotesk', sans-serif;
  font-size: ${props => props.size === 'small' ? '0.95rem' : '1.1rem'};
  color: #fff;
  background: ${props => props.selected ? '#1E1E35' : 'transparent'};
  border-radius: ${props => props.size === 'small' ? '8px' : '12px'};
  font-weight: ${props => props.selected ? 700 : 500};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  margin-bottom: 8px;
  &:last-of-type {
    margin-bottom: 0;
  }
  &:hover, &.hovered {
    background: ${props => props.selected ? '#1E1E35' : '#1E1E35'};
    color: #fff;
  }
`;

const Placeholder = styled.span`
  color: #aaa;
`;

const Select = ({ label, value, onChange, options, placeholder, size, disabled, ...props }) => {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(-1);
  const ref = useRef();

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    if (!open) {
      setHoveredIdx(-1);
    }
  }, [open]);

  useEffect(() => {
    const handleClick = e => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);

  const handleSelect = (opt) => {
    if (disabled) {
      return;
    }
    if (onChange) {
      onChange({ target: { value: opt.value } });
    }
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'Enter' || e.key === ' ')) {
      setOpen(true);
      setHoveredIdx(options.findIndex(opt => opt.value === value));
      e.preventDefault();
    } else if (open) {
      if (e.key === 'ArrowDown') {
        setHoveredIdx(idx => Math.min(options.length - 1, idx + 1));
        e.preventDefault();
      } else if (e.key === 'ArrowUp') {
        setHoveredIdx(idx => Math.max(0, idx - 1));
        e.preventDefault();
      } else if (e.key === 'Enter' && hoveredIdx >= 0) {
        handleSelect(options[hoveredIdx]);
        e.preventDefault();
      } else if (e.key === 'Escape') {
        setOpen(false);
        e.preventDefault();
      }
    }
  };

  return (
    <SelectWrapper ref={ref}>
      {label && <Label>{label}</Label>}
      <SelectContainer>
        <StyledSelectBox
          tabIndex={0}
          className={focused ? 'focused' : ''}
          size={size}
          onClick={() => !disabled && setOpen(o => !o)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-disabled={disabled}
          disabled={disabled}
          {...props}
        >
          {selectedOption ? (
            <span>{selectedOption.label}</span>
          ) : (
            <Placeholder>{placeholder || 'Select...'}</Placeholder>
          )}
          <Arrow>▼</Arrow>
        </StyledSelectBox>
        {open && !disabled && (
          <Dropdown size={size} role="listbox">
            {options.map((opt, idx) => (
              <Option
                key={opt.value}
                size={size}
                selected={opt.value === value}
                className={hoveredIdx === idx ? 'hovered' : ''}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(-1)}
                onMouseDown={e => { e.preventDefault(); handleSelect(opt); }}
                role="option"
                aria-selected={opt.value === value}
              >
                {opt.label}
              </Option>
            ))}
          </Dropdown>
        )}
      </SelectContainer>
    </SelectWrapper>
  );
};

export default Select; 